import React from 'react';
import type { RatingStaticContent } from '@core-api/presentation/types/response';
import {
  usePresentationContext,
  usePresentationSlideContext,
} from '../../legacy-context/presentationContext';
import { getVisualizationState } from '../../utils/getVisualizationState';
import { useScreenshotReady } from '../../shared/hooks/use-screenshot-ready';
import { RatingGridPresentation } from './RatingGridPresentation';
import { useRatingGridPreviewResults } from './hooks/use-rating-grid-preview-results';
import { useRatingGridResults } from './hooks/use-rating-grid-results';

export function RatingGridPresentationDataHandler() {
  const {
    InteractiveCanvasComponents,
    isPreviewResultsEnabled,
    useConfig,
    useCurrentSession,
    usePace,
  } = usePresentationContext();
  const { enableAnimations, mode } = useConfig();
  const { currentStep } = usePace();
  const slide = usePresentationSlideContext();
  const { isCurrentSessionActive, activeSessionId } = useCurrentSession();
  const staticContent = slide.staticContent as RatingStaticContent;
  const interactiveContent = slide.interactiveContents[0]!;

  const visualizationState = getVisualizationState({
    mode,
    currentStep,
  });

  const shouldHideResults =
    slide.responseVisibility === 'hidden' ||
    visualizationState === 'hide-answers';

  const range = React.useMemo(
    () => ({
      min: interactiveContent.responseRange?.min ?? 0,
      max: interactiveContent.responseRange?.max ?? 10,
    }),
    [
      interactiveContent.responseRange?.min,
      interactiveContent.responseRange?.max,
    ],
  );

  const is4Values =
    staticContent.interactiveContentStyling.visualization === 'four-values';

  const { xAxisLabels, yAxisLabels } = is4Values
    ? getHacky4x4LegacyAxisLabels({
        yAxisLabels: staticContent.interactiveContentStyling.yAxisLabels,
        xAxisLabels: staticContent.interactiveContentStyling.xAxisLabels,
      })
    : {
        xAxisLabels: staticContent.interactiveContentStyling.xAxisLabels,
        yAxisLabels: staticContent.interactiveContentStyling.yAxisLabels,
      };

  const { results, isLoading } = useRatingGridResults({
    adminKey: slide.slideAdminKey,
    interactiveContentId: interactiveContent.interactiveContentId,
    shouldFetchResults: !shouldHideResults && !isPreviewResultsEnabled,
    sessionId: isCurrentSessionActive ? undefined : activeSessionId,
  });

  const previewResults = useRatingGridPreviewResults({
    isEnabled: isPreviewResultsEnabled,
    choices: interactiveContent.choices,
    range,
  });

  useScreenshotReady({ isReady: mode === 'screenshot' && !isLoading });

  return (
    <RatingGridPresentation
      title={slide.title}
      styledTitle={staticContent.styledTitle as Record<string, unknown>}
      label={staticContent.label}
      longerDescription={interactiveContent.description}
      fontSizeOffset={staticContent.textSizeOffset}
      shouldHideResults={shouldHideResults}
      CanvasVisualizationArea={
        InteractiveCanvasComponents?.CanvasVisualizationArea
      }
      choices={interactiveContent.choices.map((choice) => ({
        id: choice.interactiveContentChoiceId,
        label: choice.title,
      }))}
      shouldAnimate={enableAnimations}
      results={isPreviewResultsEnabled ? previewResults : results}
      xAxisLabels={xAxisLabels}
      yAxisLabels={yAxisLabels}
      range={range}
      backgroundImage={
        interactiveContent.image
          ? { url: interactiveContent.image.presets.original.url }
          : null
      }
    />
  );
}

/**
 * Why did we do this?
 * We deleted the four-values visualization type without migrating existing slides.
 * Which means we lost the ability to display min/max labels, but the old slides still has these values set.
 * And Impact/Effort is set on all slides for x and y axis labels by default.
 * In order to display the old slides correctly, we need to override the axis labels and use the max values as the axis labels.
 * This is a hacky way to do it, but best Impact/Effort solution ATM.
 */
const getHacky4x4LegacyAxisLabels = ({
  xAxisLabels,
  yAxisLabels,
}: {
  xAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };
  yAxisLabels: {
    axisLabel: string;
    minimumValueLabel: string;
    maximumValueLabel: string;
  };
}) => {
  let overrideXAxisLabel = xAxisLabels.axisLabel;
  let overrideYAxisLabel = yAxisLabels.axisLabel;

  if (
    xAxisLabels.axisLabel === 'Effort' &&
    !!xAxisLabels.maximumValueLabel &&
    yAxisLabels.axisLabel === 'Impact' &&
    !!yAxisLabels.maximumValueLabel
  ) {
    overrideXAxisLabel = xAxisLabels.maximumValueLabel;
    overrideYAxisLabel = yAxisLabels.maximumValueLabel;
  }

  return {
    xAxisLabels: {
      axisLabel: overrideXAxisLabel,
      minimumValueLabel: xAxisLabels.minimumValueLabel,
      maximumValueLabel: xAxisLabels.maximumValueLabel,
    },
    yAxisLabels: {
      axisLabel: overrideYAxisLabel,
      minimumValueLabel: yAxisLabels.minimumValueLabel,
      maximumValueLabel: yAxisLabels.maximumValueLabel,
    },
  };
};
